import { useRef } from 'react';
import { Upload, Loader2, AlertTriangle, CheckCircle2 } from 'lucide-react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';

interface DocumentIntelligenceProps {
    isUploading: boolean;
    uploadError: string | null;
    documentAnalysis: any;
    onFileUpload: (event: React.ChangeEvent<HTMLInputElement>) => void;
    onResetAnalysis: () => void;
}

export function DocumentIntelligence({
    isUploading,
    uploadError,
    documentAnalysis,
    onFileUpload,
    onResetAnalysis
}: DocumentIntelligenceProps) {
    const fileInputRef = useRef<HTMLInputElement>(null);

    return (
        <div className="bg-[#0A0A0A]/50 backdrop-blur-sm p-8 rounded-3xl border border-white/5 flex flex-col h-full">
            <div className="flex items-center gap-3 mb-6">
                <div className="p-2 bg-blue-500/10 rounded-lg">
                    <Upload className="w-5 h-5 text-blue-400" />
                </div>
                <h2 className="text-xl font-bold text-white tracking-tight">Document Intelligence</h2>
            </div>

            <div className="flex-1">
                <input
                    type="file"
                    ref={fileInputRef}
                    onChange={onFileUpload}
                    accept=".pdf"
                    className="hidden"
                />

                {!documentAnalysis ? (
                    <div
                        onClick={() => fileInputRef.current?.click()}
                        className={`h-full min-h-[400px] border-2 border-dashed rounded-xl p-12 flex flex-col items-center justify-center text-center transition-all group cursor-pointer
                            ${isUploading ? 'border-blue-500/50 bg-blue-500/5 pointer-events-none' : 'border-white/10 hover:border-blue-500/50 hover:bg-white/5'}
                        `}
                    >
                        {isUploading ? (
                            <>
                                <Loader2 className="w-12 h-12 text-blue-500 animate-spin mb-4" />
                                <h3 className="text-lg font-bold text-white mb-2">Analyzing Document...</h3>
                                <p className="text-gray-400 text-sm">Gemini 1.5 Pro is reading the full PDF text...</p>
                            </>
                        ) : (
                            <>
                                <div className="w-16 h-16 bg-blue-500/10 rounded-full flex items-center justify-center mb-4 group-hover:scale-110 transition-transform">
                                    <Upload className="w-8 h-8 text-blue-400" />
                                </div>
                                <h3 className="text-lg font-bold text-white mb-2">Upload Solicitation</h3>
                                <p className="text-gray-400 text-sm max-w-sm mb-6">
                                    Drag & drop PDF files here, or click to browse.
                                    <br />
                                    <span className="text-xs text-gray-500 mt-2 block">Supports .pdf (Max 10MB)</span>
                                </p>
                                <button className="px-6 py-2 bg-blue-600 hover:bg-blue-500 text-white rounded-lg font-medium transition-colors">
                                    Select Files
                                </button>
                            </>
                        )}
                        {uploadError && (
                            <div className="mt-4 p-3 bg-red-500/10 border border-red-500/20 text-red-400 rounded-lg flex items-center gap-2 text-sm">
                                <AlertTriangle className="w-4 h-4" />
                                {uploadError}
                            </div>
                        )}
                    </div>
                ) : (
                    <div className="space-y-6 animate-in fade-in slide-in-from-bottom-4 duration-500 h-full flex flex-col">
                        <div className="bg-green-500/10 border border-green-500/20 rounded-xl p-6 mb-4">
                            <div className="flex items-center gap-3 mb-4">
                                <CheckCircle2 className="w-6 h-6 text-green-400" />
                                <h3 className="text-lg font-bold text-white">Analysis Complete</h3>
                                <button
                                    onClick={onResetAnalysis}
                                    className="ml-auto text-sm text-gray-400 hover:text-white underline"
                                >
                                    Analyze Another
                                </button>
                            </div>

                            <div className="grid grid-cols-1 gap-6">
                                <div>
                                    <h4 className="text-sm font-semibold text-blue-400 uppercase tracking-wider mb-2">Executive Summary</h4>
                                    <div className="text-gray-300 text-sm leading-relaxed prose prose-invert prose-sm max-w-none">
                                        <ReactMarkdown remarkPlugins={[remarkGfm]}>
                                            {documentAnalysis.executiveSummary}
                                        </ReactMarkdown>
                                    </div>
                                </div>
                                <div>
                                    <h4 className="text-sm font-semibold text-purple-400 uppercase tracking-wider mb-2">Win Themes</h4>
                                    <ul className="list-disc list-inside text-gray-300 text-sm space-y-1">
                                        {documentAnalysis.winThemes?.map((theme: string, i: number) => (
                                            <li key={i}>{theme}</li>
                                        ))}
                                    </ul>
                                </div>
                            </div>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div className="bg-white/5 border border-white/10 rounded-xl p-6">
                                <h4 className="text-sm font-semibold text-blue-400 uppercase tracking-wider mb-3">Technical Requirements</h4>
                                <ul className="space-y-2">
                                    {documentAnalysis.technicalRequirements?.map((tech: string, i: number) => (
                                        <li key={i} className="flex gap-2 text-sm text-gray-300">
                                            <div className="w-1.5 h-1.5 bg-blue-500 rounded-full mt-1.5 flex-shrink-0" />
                                            {tech}
                                        </li>
                                    ))}
                                </ul>
                            </div>
                            <div className="bg-white/5 border border-white/10 rounded-xl p-6">
                                <h4 className="text-sm font-semibold text-purple-400 uppercase tracking-wider mb-3">Labor Categories</h4>
                                <ul className="space-y-2">
                                    {documentAnalysis.laborCategories?.map((role: string, i: number) => (
                                        <li key={i} className="flex gap-2 text-sm text-gray-300">
                                            <div className="w-1.5 h-1.5 bg-purple-500 rounded-full mt-1.5 flex-shrink-0" />
                                            {role}
                                        </li>
                                    ))}
                                </ul>
                            </div>
                            <div className="bg-white/5 border border-white/10 rounded-xl p-6">
                                <h4 className="text-sm font-semibold text-red-400 uppercase tracking-wider mb-3">Critical Risks</h4>
                                <ul className="space-y-2">
                                    {documentAnalysis.riskAssessment?.map((risk: string, i: number) => (
                                        <li key={i} className="flex gap-2 text-sm text-gray-300">
                                            <AlertTriangle className="w-4 h-4 text-red-400 flex-shrink-0 mt-0.5" />
                                            {risk}
                                        </li>
                                    ))}
                                </ul>
                            </div>
                            <div className="bg-white/5 border border-white/10 rounded-xl p-6">
                                <h4 className="text-sm font-semibold text-yellow-400 uppercase tracking-wider mb-3">Key Dates</h4>
                                <ul className="space-y-2">
                                    {documentAnalysis.keyDates?.map((date: string, i: number) => (
                                        <li key={i} className="flex gap-2 text-sm text-gray-300 font-mono">
                                            <CheckCircle2 className="w-4 h-4 text-yellow-400 flex-shrink-0 mt-0.5" />
                                            {date}
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}
